import { Switch } from "@mui/material";
import Input from "../ui/Input";
import { Controller } from "react-hook-form";

const HomeLoanForm = ({
  control,
  watch,
  isPOI = false,
}) => {
  const isRepayingHomeLoan = watch("house_loan_applicable") === 1;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toggle Header */}
      <div className="flex items-center justify-between p-4 bg-white">
        <span className="text-base font-medium text-gray-900">
          Is the employee repaying home loan for a self occupied house property?
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {isRepayingHomeLoan ? "Yes" : "No"}
          </span>
          <Controller
            control={control}
            name="house_loan_applicable"
            render={({ field }) => (
              <Switch
                checked={field.value === 1 ? true : false}
                onChange={(e) => {
                  field.onChange(e.target.checked ? 1 : 0);
                }}
                color="primary"
                size="medium"
              />
            )}
          />
        </div>
      </div>

      {/* Home Loan Details Form */}
      {isRepayingHomeLoan && (
        <div className="border-t border-gray-200 px-6 py-6 space-y-6 bg-white">
          {isPOI ? (
            // POI Mode: 3-column layout aligned with sticky header
            <div className="space-y-6">
              {/* Principal Amount Row */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (1/3) */}
                <div className="w-1/3 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Principal Paid on Home Loan
                  </label>
                </div>

                {/* Declared Amount Column (1/3) */}
                <div className="w-1/3 flex justify-center">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.principal_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      disabled={true}
                      hideLabel={true}
                    />
                  </div>
                </div>

                {/* Actual Amount Column (1/3) */}
                <div className="w-1/3 flex justify-center">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.actual_principal_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      hideLabel={true}
                    />
                  </div>
                </div>
              </div>

              {/* Interest Amount Row */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (1/3) */}
                <div className="w-1/3 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Interest Paid on Home Loan
                  </label>
                </div>

                {/* Declared Amount Column (1/3) */}
                <div className="w-1/3 flex justify-center">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.interest_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      disabled={true}
                      hideLabel={true}
                    />
                  </div>
                </div>

                {/* Actual Amount Column (1/3) */}
                <div className="w-1/3 flex justify-center">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.actual_interest_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      hideLabel={true}
                    />
                  </div>
                </div>
              </div>

              {/* Lender Details Rows */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (1/3) */}
                <div className="w-1/3 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Name of the Lender
                  </label>
                </div>

                {/* Declared Amount Column (1/3) - spans across both columns for lender info */}
                <div className="w-2/3">
                  <Input
                    control={control}
                    name="home_loan.name_of_lender"
                    placeholder="Enter lender name"
                    hideLabel={true}
                  />
                </div>
              </div>

              <div className="flex w-full gap-4">
                {/* Particulars Column (1/3) */}
                <div className="w-1/3 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Lender PAN
                  </label>
                </div>

                {/* Declared Amount Column (1/3) - spans across both columns for lender info */}
                <div className="w-2/3">
                  <Input
                    control={control}
                    name="home_loan.lender_pan"
                    placeholder="Enter lender PAN"
                    hideLabel={true}
                  />
                </div>
              </div>
            </div>
          ) : (
            // Declaration Mode: 2-column layout with separate labels
            <div className="space-y-6">
              {/* Principal Amount Row */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (3/5) */}
                <div className="w-3/5 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Principal Paid on Home Loan
                  </label>
                </div>

                {/* Amount Column (2/5) */}
                <div className="w-2/5 flex justify-end">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.principal_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      hideLabel={true}
                    />
                  </div>
                </div>
              </div>

              {/* Interest Amount Row */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (3/5) */}
                <div className="w-3/5 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Interest Paid on Home Loan
                  </label>
                </div>

                {/* Amount Column (2/5) */}
                <div className="w-2/5 flex justify-end">
                  <div className="w-full max-w-xs">
                    <Input
                      control={control}
                      name="home_loan.interest_amount"
                      type="number"
                      isCurrency={true}
                      placeholder="0"
                      isPositiveNumber={true}
                      hideLabel={true}
                    />
                  </div>
                </div>
              </div>

              {/* Lender Details Rows */}
              <div className="flex w-full gap-4">
                {/* Particulars Column (3/5) */}
                <div className="w-3/5 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Name of the Lender
                  </label>
                </div>

                {/* Input Column (2/5) */}
                <div className="w-2/5">
                  <Input
                    control={control}
                    name="home_loan.name_of_lender"
                    placeholder="Enter lender name"
                    hideLabel={true}
                  />
                </div>
              </div>

              <div className="flex w-full gap-4">
                {/* Particulars Column (3/5) */}
                <div className="w-3/5 flex items-center">
                  <label className="text-sm font-medium text-gray-900">
                    Lender PAN
                  </label>
                </div>

                {/* Input Column (2/5) */}
                <div className="w-2/5">
                  <Input
                    control={control}
                    name="home_loan.lender_pan"
                    placeholder="Enter lender PAN"
                    hideLabel={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HomeLoanForm;
